/** Safe handler templates for admin-added terminal steps (Phase 7). */
import { isSkip } from './assistant-ticket-new.js';
import { parseSmartDate, validateSmartDate, DATE_HINT } from './assistant-datetime.js';

function parseLkrAmount(v) {
  const n = Number(String(v ?? '').trim().replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

function validateLkrAmount(v, { allowZero = false } = {}) {
  const n = parseLkrAmount(v);
  if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR.';
  if (!allowZero && n <= 0) return 'Enter amount in LKR.';
  return null;
}

export const HANDLER_TEMPLATES = {
  text_optional: {
    id: 'text_optional',
    label: 'Optional text',
    description: 'Free text — agents can skip with -',
    defaultOptional: true,
  },
  text_required: {
    id: 'text_required',
    label: 'Required text',
    description: 'Non-empty text answer',
    defaultOptional: false,
  },
  lkr_amount_optional: {
    id: 'lkr_amount_optional',
    label: 'Optional LKR amount',
    description: 'Money in LKR — skip with -',
    defaultOptional: true,
  },
  numbered_choice: {
    id: 'numbered_choice',
    label: 'Numbered choice',
    description: 'Agent picks 1, 2, 3… from a list you define',
    defaultOptional: false,
    needsChoices: true,
  },
  date_optional: {
    id: 'date_optional',
    label: 'Optional date',
    description: 'Smart date — skip with -',
    defaultOptional: true,
  },
};

export function listHandlerTemplates() {
  return Object.values(HANDLER_TEMPLATES);
}

/** Simulator / happy-path suggestion for a custom handler step. */
export function suggestHandlerAnswer(handlerId, config = {}, mode = 'happy', questionMeta = {}) {
  const optional = config.optional != null ? config.optional : HANDLER_TEMPLATES[handlerId]?.defaultOptional;
  if (mode === 'skip') return optional ? '-' : null;
  if (mode === 'default' && questionMeta?.defaultValue != null && questionMeta.defaultValue !== '') {
    return String(questionMeta.defaultValue);
  }
  if (mode === 'invalid') {
    if (handlerId === 'lkr_amount_optional') return '-100';
    if (handlerId === 'numbered_choice') return '99';
    if (handlerId === 'date_optional') return 'bad-date';
    return '!!!';
  }
  if (handlerId === 'text_optional' || handlerId === 'date_optional' || handlerId === 'lkr_amount_optional') {
    return optional ? '-' : (handlerId === 'lkr_amount_optional' ? '25000' : handlerId === 'date_optional' ? '260801' : 'Sample');
  }
  if (handlerId === 'text_required') return 'Sample answer';
  if (handlerId === 'numbered_choice') return '1';
  return optional ? '-' : '1';
}

function parseNumberedChoice(raw, choices) {
  const n = Number(String(raw).trim());
  if (Number.isInteger(n) && n >= 1 && n <= choices.length) return choices[n - 1];
  return String(raw).trim();
}

function validateNumberedChoice(raw, choices) {
  const n = Number(String(raw).trim());
  if (Number.isInteger(n) && n >= 1 && n <= choices.length) return null;
  if (String(raw).trim().length >= 1) return null;
  return `Pick 1–${choices.length} or type a value.`;
}

/** Build a runtime step from an admin custom-step config. */
export function buildHandlerStep(config, ctx = {}) {
  const handler = HANDLER_TEMPLATES[config.handler];
  if (!handler) return null;

  const optional = config.optional != null ? !!config.optional : handler.defaultOptional;
  const ask = config.ask || 'Answer?';
  const hint = config.hint || '';
  const key = config.stepKey;
  const choices = String(config.choices || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  const base = {
    key,
    ask,
    hint,
    optional,
    _custom: true,
    _handler: config.handler,
  };

  if (config.handler === 'text_optional') {
    return {
      ...base,
      validate: (v) => (optional && isSkip(v) ? null : (!String(v).trim() ? 'Enter text or skip with -.' : null)),
      parse: (v) => (optional && isSkip(v) ? null : String(v).trim()),
      apply: (val) => {
        if (ctx.mode !== 'new' || !ctx.data) return;
        ctx.data._customFields = ctx.data._customFields || {};
        ctx.data._customFields[key] = val;
      },
    };
  }

  if (config.handler === 'text_required') {
    return {
      ...base,
      optional: false,
      validate: (v) => (!String(v).trim() ? 'Required.' : null),
      parse: (v) => String(v).trim(),
      apply: (val) => {
        if (ctx.mode !== 'new' || !ctx.data) return;
        ctx.data._customFields = ctx.data._customFields || {};
        ctx.data._customFields[key] = val;
      },
    };
  }

  if (config.handler === 'lkr_amount_optional') {
    return {
      ...base,
      validate: (v) => (optional && isSkip(v) ? null : validateLkrAmount(v)),
      parse: (v) => (optional && isSkip(v) ? null : Math.round(parseLkrAmount(v))),
      apply: (val) => {
        if (ctx.mode !== 'new' || !ctx.data) return;
        ctx.data._customFields = ctx.data._customFields || {};
        ctx.data._customFields[key] = val;
      },
    };
  }

  if (config.handler === 'numbered_choice') {
    if (choices.length < 2) return null;
    const choiceHint = choices.map((c, i) => `${i + 1}. ${c}`).join('  ');
    return {
      ...base,
      hint: hint ? `${hint}\n${choiceHint}` : choiceHint,
      validate: (v) => (optional && isSkip(v) ? null : validateNumberedChoice(v, choices)),
      parse: (v) => (optional && isSkip(v) ? null : parseNumberedChoice(v, choices)),
      apply: (val) => {
        if (ctx.mode !== 'new' || !ctx.data) return;
        ctx.data._customFields = ctx.data._customFields || {};
        ctx.data._customFields[key] = val;
      },
    };
  }

  if (config.handler === 'date_optional') {
    return {
      ...base,
      hint: hint || DATE_HINT,
      validate: (v) => (optional && isSkip(v) ? null : validateSmartDate(v)),
      parse: (v) => (optional && isSkip(v) ? null : parseSmartDate(v)),
      apply: (val) => {
        if (ctx.mode !== 'new' || !ctx.data) return;
        ctx.data._customFields = ctx.data._customFields || {};
        ctx.data._customFields[key] = val;
      },
    };
  }

  return null;
}

export function mergeCustomFieldsIntoExtra(extra, customFields) {
  if (!customFields || !Object.keys(customFields).length) return extra;
  return { ...extra, custom_steps: { ...(extra.custom_steps || {}), ...customFields } };
}

export function pickCustomStageAnswers(flowId, answers = {}, customConfigs = []) {
  const out = {};
  customConfigs.forEach((cfg) => {
    if (cfg.flowId !== flowId) return;
    const val = answers[cfg.stepKey];
    if (val != null && val !== '') out[cfg.stepKey] = val;
  });
  return out;
}
